import { z } from 'zod'

// Schema pour un jalon de paiement
const milestoneSchema = z.object({
  label: z.string().min(1, 'Libellé requis'),
  percentage: z.number().min(0, 'Min 0%').max(100, 'Max 100%'),
  trigger: z.string().optional(),
  estimatedDate: z.string().optional(),
})

// Schema de base sans refinements pour les types
const basePaymentScheduleSchema = z.object({
  // Section financière
  totalAmount: z.number().min(1, 'Montant total requis'),
  tvaRate: z.number().min(0).max(100).optional().default(20),
  currency: z.string().optional().default('EUR'),

  // Conditions de paiement
  paymentTerms: z.enum(['immediate', '30_days', '45_days', '60_days', 'custom']),
  customPaymentDays: z.number().optional(),
  paymentMethod: z
    .array(z.enum(['bank_transfer', 'check', 'card', 'direct_debit']))
    .min(1, 'Au moins un mode de paiement requis'),
  latePenaltyRate: z.number().min(0).optional().default(12.15), // Taux légal 2026

  // Jalons de paiement
  milestones: z.array(milestoneSchema).min(1, 'Au moins un jalon requis'),

  // Informations client
  clientName: z.string().min(1, 'Nom du client requis'),
  clientSiren: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{9}$/.test(val), 'SIREN invalide (9 chiffres)'),
  clientAddress: z.string().optional(),
  clientEmail: z.string().email('Email invalide').optional().or(z.literal('')),

  // Coordonnées bancaires (optionnel)
  bankDetails: z
    .object({
      iban: z.string().optional(),
      bic: z.string().optional(),
      bankName: z.string().optional(),
    })
    .optional(),

  // Options de génération
  includeVatDetails: z.boolean().optional().default(true),
  includeLegalMentions: z.boolean().optional().default(true),
  includePaymentConditions: z.boolean().optional().default(true),
})

// Schema principal avec refinements
export const paymentScheduleFormSchema = basePaymentScheduleSchema
  .refine(
    (data) => {
      // Vérifier que la somme des pourcentages = 100%
      const total = data.milestones.reduce((sum, m) => sum + m.percentage, 0)
      return Math.abs(total - 100) < 0.01
    },
    {
      message: 'La somme des pourcentages doit être égale à 100%',
      path: ['milestones'],
    }
  )
  .refine(
    (data) => {
      // Si paymentTerms est 'custom', customPaymentDays doit être défini
      if (data.paymentTerms === 'custom') {
        return data.customPaymentDays !== undefined && data.customPaymentDays > 0
      }
      return true
    },
    {
      message: 'Nombre de jours requis pour un délai personnalisé',
      path: ['customPaymentDays'],
    }
  )

// Type basé sur le schema de base (sans refinements) pour éviter les problèmes de typage
export type PaymentScheduleFormData = z.infer<typeof basePaymentScheduleSchema>

// Valeurs par défaut pour le formulaire
export const defaultPaymentScheduleValues: PaymentScheduleFormData = {
  totalAmount: 0,
  tvaRate: 20,
  currency: 'EUR',
  paymentTerms: '30_days',
  latePenaltyRate: 12.15, // Taux BCE + 10 points (2026)
  paymentMethod: ['bank_transfer'],
  milestones: [
    { label: 'Acompte à la commande', percentage: 30, trigger: 'Signature du devis' },
    { label: 'Livraison intermédiaire', percentage: 40, trigger: 'Validation fonctionnelle' },
    { label: 'Solde à la livraison', percentage: 30, trigger: 'Mise en production' },
  ],
  clientName: '',
  includeVatDetails: true,
  includeLegalMentions: true,
  includePaymentConditions: true,
}

// Labels pour l'affichage
export const paymentTermsLabels: Record<string, string> = {
  immediate: 'Paiement immédiat',
  '30_days': '30 jours net',
  '45_days': '45 jours fin de mois',
  '60_days': '60 jours net',
  custom: 'Délai personnalisé',
}

export const paymentMethodLabels: Record<string, string> = {
  bank_transfer: 'Virement bancaire',
  check: 'Chèque',
  card: 'Carte bancaire',
  direct_debit: 'Prélèvement SEPA',
}
